import { z } from "zod";

export const agaveShadowRenderStatusSchema = z.enum(["queued", "processing", "complete", "failed"]);
export const agaveShadowRenderModeSchema = z.enum(["low", "medium", "high"]);
export const agaveShadowRenderCallerSchema = z.enum(["ios", "web", "android", "oryx", "unknown"]);
export const agaveShadowRenderProductTypeSchema = z.enum([
  "REAL_ESTATE",
  "AI_EDIT",
  "CAPTIONS",
  "MUSIC",
  "LONG_TO_SHORT",
  "VOICEOVER",
  "AVATAR",
  "PRODUCT_AD",
]);

export type AgaveShadowRenderStatus = z.infer<typeof agaveShadowRenderStatusSchema>;
export type AgaveShadowRenderMode = z.infer<typeof agaveShadowRenderModeSchema>;
export type AgaveShadowRenderCaller = z.infer<typeof agaveShadowRenderCallerSchema>;
export type AgaveShadowRenderProductType = z.infer<typeof agaveShadowRenderProductTypeSchema>;

export const agaveShadowRenderAssetSchema = z.object({
  id: z.string(),
  type: z.string(),
  url: z.string(),
  createdAt: z.number(),
});

export type AgaveShadowRenderAsset = z.infer<typeof agaveShadowRenderAssetSchema>;

export const agaveShadowRenderSummarySchema = z.object({
  id: z.string(),
  agaveOperationId: z.string(),
  status: agaveShadowRenderStatusSchema,
  createdAt: z.number(),
  updatedAt: z.number().nullish(),
  userId: z.string().nullish(),
  orgId: z.string().nullish(),
  caller: agaveShadowRenderCallerSchema.or(z.string()).nullish(),
  sourceWorkflowName: z.string(),
  sourceWorkflowId: z.string(),
  sourceVideoAssetId: z.string().nullish(),
  sourceProjectId: z.string().nullish(),
  sourceCaptionsProjectId: z.string().nullish(),
  sourceProjectProtoUri: z.string().nullish(),
  sourceProjectProtoUrl: z.string().nullish(),
  sourceProjectAssetUrlMap: z.record(z.string()).nullish(),
  originalOperationId: z.string().nullish(),
  originalChargeGroupId: z.string().nullish(),
  productType: agaveShadowRenderProductTypeSchema.or(z.string()).nullish(),
  sourceVideoAsset: agaveShadowRenderAssetSchema.nullish(),
  agaveWorkflowId: z.string().nullish(),
  agaveWorkflowRunId: z.string().nullish(),
  agaveChargeGroupId: z.string().nullish(),
  originalCreditsCharged: z.number().nullish(),
  originalMeteredCredits: z.number().nullish(),
  originalChargeCount: z.number().nullish(),
  agaveCreditsCharged: z.number().nullish(),
  agaveMeteredCredits: z.number().nullish(),
  agaveChargeCount: z.number().nullish(),
  goal: z.string().nullish(),
  mode: agaveShadowRenderModeSchema.or(z.string()).nullish(),
  projectProtoUri: z.string().nullish(),
  captionsProjectId: z.string().nullish(),
  projectDataUri: z.string().nullish(),
  projectDataUrl: z.string().nullish(),
  projectAssetUrlMap: z.record(z.string()).nullish(),
  videoAssetId: z.string().nullish(),
  shadowVideoAsset: agaveShadowRenderAssetSchema.nullish(),
  error: z.string().nullish(),
});

export type AgaveShadowRenderSummary = z.infer<typeof agaveShadowRenderSummarySchema>;

export const agaveShadowRenderCreditsSchema = z.object({
  originalCreditsCharged: z.number().nullish(),
  originalMeteredCredits: z.number().nullish(),
  originalChargeCount: z.number().nullish(),
  agaveCreditsCharged: z.number().nullish(),
  agaveMeteredCredits: z.number().nullish(),
  agaveChargeCount: z.number().nullish(),
});

export type AgaveShadowRenderCredits = z.infer<typeof agaveShadowRenderCreditsSchema>;

export const agaveShadowRendersListResponseSchema = z.object({
  results: z.object({
    page: z.array(agaveShadowRenderSummarySchema),
    pageInfo: z.object({
      hasNextPage: z.boolean(),
      endCursor: z.string().nullable(),
    }),
  }),
});

export const agaveShadowRenderDetailResponseSchema = z.object({
  result: agaveShadowRenderSummarySchema,
  operation: z
    .record(z.unknown())
    .nullish()
    .transform((operation) => operation ?? {}),
  credits: agaveShadowRenderCreditsSchema.nullish(),
});

export type AgaveShadowRenderDetailResponse = z.infer<typeof agaveShadowRenderDetailResponseSchema>;
